/**
 * Wire-shape force-field data delivered by bfd-server's extended
 * `get-forcefield-params` response (v6 schema). Per-atom arrays have
 * length equal to the residue's `atomNames`. Atom indices in the term
 * arrays are local to the ligand (0 .. nAtoms-1) and follow the same
 * ordering as `atomNames`.
 *
 * Consumed by `LocalMinimizer/forcefield/getTypedParameters.js` to
 * project into the engine's `LigandParameters` shape (flat Float64Arrays).
 *
 * Units: nm, kJ/mol, rad.
 *
 * @typedef {Object} RawForcefieldData
 * @property {string}        forcefieldVersion  e.g. "gaff-2.11" or
 *                                              "openff-sage-2.3". Opaque
 *                                              metadata for cache invalidation.
 * @property {Float64Array}  sigma              Per atom, nm.
 * @property {Float64Array}  epsilon            Per atom, kJ/mol.
 * @property {number}        scee               1-4 Coulomb scaling divisor
 *                                              (AMBER SCEE, default 1.2).
 *                                              Engine applies 1/scee.
 * @property {number}        scnb               1-4 LJ scaling divisor
 *                                              (AMBER SCNB, default 2.0).
 *                                              Engine applies 1/scnb.
 * @property {Array<{i:number,j:number,k:number,r0:number}>} bonds
 * @property {Array<{
 *   i:number,j:number,k:number,kForce:number,theta0:number
 * }>} angles
 * @property {Array<{
 *   i:number,j:number,k:number,l:number,
 *   periodicity:number,phase:number,kForce:number
 * }>} torsions
 * @property {Array<{
 *   i:number,j:number,k:number,l:number,
 *   periodicity:number,phase:number,kForce:number
 * }>} impropers
 * @property {Int32Array}    exclusions         Length 2*N, packed (i,j) pairs;
 *                                              1-2 and 1-3.
 * @property {Int32Array|null} scaledPairs      Optional. Length 2*N packed
 *                                              (i,j) 1-4 pairs when the server
 *                                              supplies an authoritative list;
 *                                              null when omitted, in which
 *                                              case the client derives them
 *                                              from the bond graph.
 */

/**
 * @class ResidueAtomInfo
 * @description Class to maintain atom information for ligands and user compounds.
 * @member atomNames Map of ligand res name / compound name to atom names
 * @member amberNames Map of ligand res name / compound name to atom amber names
 * @member charges Map of ligand res name / compound name to atom charges
 * @member ringOrdinals Map of ligand res name / compound name to atom ring ordinals
 * @member rawForcefieldData Optional force-field data from v6 server response.
 *                           See {@link RawForcefieldData}.
 */
export default class AtomInfoMap {
    constructor() {
        /** @type { Map<string, {
         *      atomNames: string[],
         *      amberNames: string[],
         *      charges: number[]?,
         *      ringOrdinals: number[]?,
         *      formalCharge: number?,
         *      rawForcefieldData: RawForcefieldData?
         * }>}
         */
        this.map = new Map();
    }

    get size() { return this.map.size; }

    has(resName) { return this.map.has(resName); }

    setAtomInfo(resName, {
        atomNames, amberNames, charges, ringOrdinals, formalCharge,
    }={}) {
        if (!resName) {
            console.error("Can't add residue atom info without residue name.");
            return;
        }

        if (!atomNames || atomNames.length === 0) {
            console.error(`Can't add residue atom info for residue ${resName} without atom names`);
            return;
        }

        this.map.set(resName, {
            atomNames,
            amberNames: this.checkArray(amberNames, 'amberNames', resName, atomNames),
            charges: this.checkArray(charges, 'charges', resName, atomNames),
            ringOrdinals: this.checkArray(ringOrdinals, 'ringOrdinals', resName, atomNames, true),
            formalCharge,
        });
    }

    updateAtomInfo(resName, newAtomInfo) {
        const existing = this.map.get(resName);
        if (!existing) {
            console.warn(`Attempted to update non-existent atom info for ${resName}`);
            return;
        }

        for (const key of ['amberNames', 'charges', 'ringOrdinals']) {
            const allowEmpty = key === 'ringOrdinals';
            if (this.checkArray(newAtomInfo[key], key, existing.atomNames, resName, allowEmpty)) {
                existing[key] = newAtomInfo[key];
                console.log(`Updated ${key} for ${resName}`);
            }
        }
    }

    getAtomInfo(resName) {
        const found = this.map.get(resName);
        return found && { ...found };
    }

    lookupFormalCharge(resName) {
        return this.getAtomInfo(resName)?.formalCharge;
    }

    /**
     * Attach (or replace) raw force-field data for a residue.
     * The entry must already exist (created via setAtomInfo).
     * @param {string} resName
     * @param {RawForcefieldData} data
     */
    setRawForcefieldData(resName, data) {
        const existing = this.map.get(resName);
        if (!existing) {
            console.warn(`Cannot set forcefield data for unknown residue ${resName}`);
            return;
        }
        existing.rawForcefieldData = data;
    }

    /**
     * @param {string} resName
     * @returns {RawForcefieldData | undefined}
     */
    getRawForcefieldData(resName) {
        return this.map.get(resName)?.rawForcefieldData;
    }

    /** @param {string} resName */
    hasRawForcefieldData(resName) {
        return this.map.get(resName)?.rawForcefieldData != null;
    }

    /**
     * Drop the cached v6 raw force-field data for every residue, forcing the
     * next `getTypedParameters` call to re-fetch from the server. Used on a
     * force-field switch (Amber<->OpenFF): the cached bonded/nonbonded terms are
     * force-field-specific, and the re-fetch also refreshes the v5 charges
     * (which likewise differ between force fields). The atomNames/charges entries
     * themselves are left in place; the re-fetch overwrites them.
     */
    clearAllRawForcefieldData() {
        for (const entry of this.map.values()) {
            entry.rawForcefieldData = undefined;
        }
    }

    /**
     * Drop the cached v6 raw force-field data for a single residue (no-op if the
     * residue isn't present). Used when that residue's connectivity changed under
     * a reused resname (compound rename / fragment-grow assemble) so the next
     * `getTypedParameters` re-fetches instead of projecting stale bonded terms
     * onto the new atom set.
     * @param {string} resName
     */
    clearRawForcefieldData(resName) {
        const entry = this.map.get(resName);
        if (entry) entry.rawForcefieldData = undefined;
    }

    // Internal functions
    checkArray(array, arrayName, resName, atomNames, allowEmpty=true) {
        if (!array || (array.length === 0 && allowEmpty)) {
            // Return false to avoid adding, but don't need to log error
            return null;
        } else if (array.length !== atomNames.length) {
            console.error(`Can't add ${arrayName} for residue ${resName} because ${arrayName} length differs from atomNames.`);
            console.error(`atomNames (${atomNames.length}): ${atomNames.join(', ')}`);
            console.error(`${arrayName} (${array.length}): ${array.join(', ')}`);
            return null;
        }
        return array;
    }
}
